import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Container, ArrowLink } from "@/components/shared/PageHeader";
import { Img, SIZES } from "@/components/shared/Img";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";

/** The board's own photograph of the entrance hall (image library). */
const ENTRANCE_PHOTO = "/api/images/af47b95c-03cb-48c7-8ada-21a354253d43";
import { errorMessage, fetchSite, sendContact } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Seo } from "@/components/shared/Seo";

const SUBJECTS = ["Booking og utleige", "Arrangement", "Bubilparkering", "Dugnad og frivillig arbeid", "Fast brukar / lag", "Anna"];
const EMPTY = { name: "", email: "", phone: "", subject: "", message: "", website: "" };

const FieldError = ({ msg, id }) => (msg ? <p className="mt-1.5 text-sm text-destructive" data-testid={id}>{msg}</p> : null);
const L = ({ htmlFor, children, optional }) => <Label htmlFor={htmlFor} className="text-[13px] font-medium">{children}{optional && <span className="font-normal text-muted-foreground"> ({optional})</span>}</Label>;

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const mutation = useMutation({
    mutationFn: sendContact,
    onSuccess: (data) => { setDone(data); toast.success("Meldinga er sendt!"); },
    onError: (err) => toast.error(errorMessage(err, "Kunne ikkje sende meldinga")),
  });

  const submit = (e) => {
    e.preventDefault();
    const er = {};
    if (form.name.trim().length < 2) er.name = "Skriv inn namnet ditt";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) er.email = "Skriv inn ei gyldig e-postadresse";
    if (!form.subject) er.subject = "Vel kva det gjeld";
    if (form.message.trim().length < 5) er.message = "Skriv ei kort melding";
    setErrors(er);
    if (Object.keys(er).length) { toast.error("Sjekk felta som er merka brandt"); return; }
    mutation.mutate({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() || null, subject: form.subject, message: form.message.trim(), website: form.website || null });
  };

  if (done) {
    return (
      <div data-testid="contact-success">
        <p className="flex items-center gap-2 text-[15px] font-medium"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Meldinga er sendt</p>
        <h2 className="t-h2 mt-4">Takk for meldinga!</h2>
        <p className="mt-4 max-w-md text-[17px] leading-relaxed text-muted-foreground">Styret har fått meldinga di og svarar på e-post så snart som råd. Huset vert drive på dugnad, så det kan ta nokre dagar.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button variant="outline" size="lg" onClick={() => { setDone(null); setForm(EMPTY); }} data-testid="contact-new-message-button">Send ei ny melding</Button>
          <Button asChild size="lg"><Link to="/">Til framsida <ArrowUpRight className="h-4 w-4" /></Link></Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="contact-form">
      <h2 className="t-h3">Send oss ei melding</h2>
      <p className="mt-1.5 text-[15px] text-muted-foreground">Vil du leige rom eller arrangere noko? Bruk heller <Link to="/leig-rom" className="font-medium text-foreground u-link">leigeskjemaet</Link> eller <Link to="/arranger/soknad" className="font-medium text-foreground u-link">arrangørskjemaet</Link> – då får styret alt dei treng med ein gong.</p>

      <div className="mt-7">
        <L>Kva gjeld det? *</L>
        <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" data-testid="contact-subject-select">
          {SUBJECTS.map((s) => (
            <button key={s} type="button" role="radio" aria-checked={form.subject === s} onClick={() => setForm((f) => ({ ...f, subject: s }))} className={cn("inline-flex h-11 items-center rounded-[8px] border px-4 text-[14px] font-medium transition-colors", form.subject === s ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:border-foreground/40")} data-testid={`contact-subject-${s}`}>{s}</button>
          ))}
        </div>
        <FieldError msg={errors.subject} id="contact-error-subject" />
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div><L htmlFor="c-name">Namn *</L><Input id="c-name" value={form.name} onChange={set("name")} className="mt-1.5 h-12" aria-required="true" data-testid="contact-name-input" /><FieldError msg={errors.name} id="contact-error-name" /></div>
        <div><L htmlFor="c-email">E-post *</L><Input id="c-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5 h-12" aria-required="true" data-testid="contact-email-input" /><FieldError msg={errors.email} id="contact-error-email" /></div>
        <div className="sm:col-span-2"><L htmlFor="c-phone" optional="valfritt">Telefon</L><Input id="c-phone" type="tel" value={form.phone} onChange={set("phone")} className="mt-1.5 h-12" data-testid="contact-phone-input" /></div>
        <div className="sm:col-span-2"><L htmlFor="c-message">Melding *</L><Textarea id="c-message" rows={6} value={form.message} onChange={set("message")} className="mt-1.5 rounded-[8px] px-3.5 py-3" aria-required="true" data-testid="contact-message-input" /><FieldError msg={errors.message} id="contact-error-message" /></div>
        <div className="hidden" aria-hidden="true"><label htmlFor="c-website">Nettside</label><input id="c-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></div>
      </div>
      <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center">
        <Button type="submit" size="xl" disabled={mutation.isPending} data-testid="contact-submit-button">{mutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sender…</> : <>Send melding <ArrowUpRight className="h-4 w-4" /></>}</Button>
        <p className="text-sm text-muted-foreground">Vi delar aldri opplysningane dine med andre.</p>
      </div>
    </form>
  );
};

export default function Contact() {
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const email = site?.contact_email || "post@halsnoykulturhus.no";
  const mapsUrl = "https://www.google.com/maps/search/?api=1&query=Halsn%C3%B8y+Kulturhus+S%C3%A6b%C3%B8vik";
  const embedUrl = "https://www.google.com/maps?q=S%C3%A6b%C3%B8vik%2C%20Halsn%C3%B8y%2C%20Kvinnherad&z=13&output=embed";

  return (
    <div data-testid="contact-page">
      <Seo title="Kontakt" description={`Ta kontakt med styret i Halsnøy Kulturhus SA på ${email} – om booking, arrangement, dugnad eller spørsmål om huset. Adresse: ${site?.address || "Sæbøvik, 5454 Sæbøvik"}.`} />
      <section data-testid="page-header">
        <Container className="pb-8 pt-10 sm:pt-16 lg:pt-20">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10">
            <div className="lg:col-span-8">
              <p className="t-mono text-muted-foreground">Kontakt</p>
              <h1 className="t-h1 mt-4 text-balance" data-testid="page-title">Ta kontakt med oss</h1>
              <a href={`mailto:${email}`} className="group mt-8 inline-flex max-w-full items-center gap-3 font-display text-[clamp(22px,3vw,44px)] font-medium tracking-[-0.03em]" data-testid="contact-email-link">
                <span className="break-all u-hover">{email}</span>
                <ArrowUpRight className="h-7 w-7 shrink-0 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 sm:h-9 sm:w-9" />
              </a>
            </div>
            <p className="t-lead max-w-md text-muted-foreground text-pretty lg:col-span-4 lg:pb-3">Huset vert drive på dugnad av styret og Velforeininga. E-post er den sikraste vegen – skriv til oss, så svarar vi så snart som råd.</p>
          </div>
        </Container>
      </section>

      <Container className="grid gap-10 py-8 sm:py-10 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7"><ContactForm /></div>
        <aside className="lg:col-span-4 lg:col-start-9">
          <dl data-testid="contact-card">
            <div className="rule-b py-4"><dt className="t-mono text-muted-foreground">E-post</dt><dd className="mt-1.5"><a href={`mailto:${email}`} className="break-all text-[17px] font-medium u-link">{email}</a><p className="mt-1 text-sm text-muted-foreground">Booking, utleige, arrangement og generelle spørsmål.</p></dd></div>
            <div className="rule-b py-4"><dt className="t-mono text-muted-foreground">Telefon</dt><dd className="mt-1.5">{site?.phone ? <a href={`tel:${site.phone}`} className="text-[17px] font-medium tabular u-link" data-testid="contact-phone-link">{site.phone}</a> : <p className="text-[15px] text-foreground/80" data-testid="contact-phone-missing">Telefonnummer kjem – bruk e-post i mellomtida.</p>}</dd></div>
            <div className="rule-b py-4"><dt className="t-mono text-muted-foreground">Adresse</dt><dd className="mt-1.5"><p className="text-[17px] font-medium" data-testid="contact-address">{site?.address || "Sæbøvik, Halsnøy"}</p><a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm font-medium u-link" data-testid="contact-maps-link">Vis i Google Maps <ArrowUpRight className="h-3.5 w-3.5" /></a></dd></div>
            <div className="py-4"><dt className="t-mono text-muted-foreground">Følg oss</dt><dd className="mt-1.5 flex gap-5 text-[15px]">
              {site?.facebook_url && <a href={site.facebook_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium u-link" data-testid="contact-facebook-link">Facebook <ArrowUpRight className="h-3.5 w-3.5" /></a>}
              {site?.instagram_url && <a href={site.instagram_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium u-link" data-testid="contact-instagram-link">Instagram <ArrowUpRight className="h-3.5 w-3.5" /></a>}
            </dd></div>
          </dl>

          <div className="mt-6 overflow-hidden rounded-[20px] bg-secondary" data-testid="contact-map">
            <iframe title="Kart over Sæbøvik, Halsnøy" src={embedUrl} className="aspect-[4/3] w-full border-0 grayscale-[30%]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
          </div>

          <figure className="mt-6" data-testid="contact-entrance-photo">
            <div className="img-hover relative overflow-hidden rounded-[20px] bg-secondary">
              <Img src={ENTRANCE_PHOTO} alt="Hovudinngangen i kulturhuset med heis" width={800} sizes={SIZES.third} className="aspect-[4/3] w-full object-cover" />
              <PhotoNumberTag src={ENTRANCE_PHOTO} interactive className="absolute right-2.5 top-2.5" testId="contact-entrance-photo-number" />
            </div>
            <figcaption className="mt-3 text-[13px] text-muted-foreground">Hovudinngangen – heisen står rett innanfor døra.</figcaption>
          </figure>

          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2">
            <ArrowLink to="/leig-rom" data-testid="contact-booking-link">Til leigeskjemaet</ArrowLink>
            <ArrowLink to="/kalender" data-testid="contact-calendar-link">Ledige datoar</ArrowLink>
          </div>
        </aside>
      </Container>
    </div>
  );
}
